import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { supportedLanguages } from '../config/languages.js';
import { localizePath, stripLanguageFromPath } from '../utils/localizedRoutes.js';

// The first-visit consent dialog (IDD D-HP-SITE-027, D-SAAS-176): "accept all" is the focused main
// action, "necessary only" stands beside it at the same size, and the two optional categories start
// switched off under "settings". On the privacy statement and the legal notice the dialog is a panel
// at the bottom that leaves the text readable.
const LEGAL_PAGES = ['/privacy', '/impressum'];

const Switch = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative w-12 h-6 shrink-0 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-border'} ${disabled ? 'cursor-not-allowed' : ''}`}
  >
    <span className={`absolute top-1 w-4 h-4 bg-text-white rounded-full transition-transform ${checked ? 'right-1' : 'left-1'}`} />
  </button>
);

const CookieConsent = ({ consent, onAcceptAll, onRejectAll, onSave, onClose }) => {
  const { t, setLanguage, currentLang } = useTranslation();
  const location = useLocation();
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choice, setChoice] = useState({
    statistics: !!consent?.statistics,
    marketing: !!consent?.marketing,
  });
  const menuRef = useRef(null);
  const acceptRef = useRef(null);
  const decided = !!consent;
  const onLegalPage = LEGAL_PAGES.includes(stripLanguageFromPath(location.pathname));
  const c = t.cookies || {};
  const legal = t.footer?.legal || {};
  const description = (c.description || '').replace('{settings}', legal.cookieSettings || '');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setIsLanguageMenuOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === 'Escape' && decided) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [decided, onClose]);

  // D-HP-SITE-027: "accept all" is the focused main action, but focusing it must not scroll the dialog past its
  // title and text — a person reads what they consent to before the decision (D-HP-SITE-030, large system font).
  useEffect(() => {
    acceptRef.current?.focus({ preventScroll: true });
  }, []);

  // "Visitor analysis" builds on "statistics": switching it on switches statistics on, and
  // switching statistics off switches it off.
  const toggle = (key) => {
    setChoice((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (key === 'marketing' && next.marketing) next.statistics = true;
      if (key === 'statistics' && !next.statistics) next.marketing = false;
      return next;
    });
  };

  const languages = supportedLanguages.map((language) => ({
    ...language,
    name: t.nav?.languageNames?.[language.code] || language.code.toUpperCase()
  }));

  const categories = [
    { key: 'necessary', checked: true, disabled: true },
    { key: 'statistics', checked: choice.statistics },
    { key: 'marketing', checked: choice.marketing },
  ];

  const panel = (
    <div className="relative bg-bg-card rounded-2xl shadow-professional max-w-xl w-full min-w-0 border border-border">
      <div className="p-6 space-y-5">
        <div className="cookie-consent__head flex justify-between items-start gap-4">
          <div className="min-w-0">
            <div className="editorial-kicker mb-4" aria-hidden="true" />
            <h2 id="cookie-consent-title" className="text-3xl font-black tracking-[-0.04em] text-text-primary break-words">{c.title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className="flex items-center space-x-1 px-3 py-2 rounded-full bg-bg-hover border border-border hover:text-primary transition-colors"
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              >
                <span className="text-sm font-bold text-text-secondary">{currentLang.toUpperCase()}</span>
                <ChevronDown size={16} className="text-text-muted" />
              </button>
              {isLanguageMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-bg-card rounded-2xl shadow-professional z-[100] border border-border overflow-hidden">
                  <div className="py-1">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          setLanguage(lang.code);
                          setIsLanguageMenuOpen(false);
                        }}
                        className="flex items-center px-4 py-2 text-text-secondary hover:bg-bg-hover text-left space-x-2 w-full"
                      >
                        <img src={lang.flag} alt={lang.name} className="w-5 h-5" />
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-text-secondary leading-relaxed">{description}</p>
        <p className="text-sm flex gap-4">
          <Link to={localizePath('/privacy', currentLang)} className="underline underline-offset-4">{legal.privacy}</Link>
          <Link to={localizePath('/impressum', currentLang)} className="underline underline-offset-4">{legal.imprint}</Link>
        </p>

        {showSettings && (
          <div className="space-y-3">
            {categories.map((cat) => (
              <div key={cat.key} className="flex items-center justify-between gap-4 p-4 bg-bg-hover rounded-2xl border border-border">
                <div className="flex-1">
                  <h3 className="font-bold text-text-primary">{c[cat.key]?.title}</h3>
                  <p className="text-sm text-text-muted">{c[cat.key]?.description}</p>
                  {cat.disabled && <p className="text-xs text-text-muted mt-1">{c.alwaysOn}</p>}
                </div>
                <Switch
                  checked={cat.checked}
                  disabled={cat.disabled}
                  label={c[cat.key]?.title}
                  onChange={() => !cat.disabled && toggle(cat.key)}
                />
              </div>
            ))}
            <button type="button" onClick={() => onSave(choice, currentLang)} className="homepage-button-secondary w-full">
              {c.save}
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <button type="button" ref={acceptRef} onClick={() => onAcceptAll(currentLang)} className="homepage-button-primary flex-1">
            {c.acceptAll}
          </button>
          <button type="button" onClick={() => onRejectAll(currentLang)} className="homepage-button-secondary flex-1">
            {c.rejectAll}
          </button>
        </div>
        {!showSettings && (
          <button type="button" onClick={() => setShowSettings(true)} className="text-sm underline underline-offset-4 text-text-secondary">
            {c.settings}
          </button>
        )}
      </div>
    </div>
  );

  if (onLegalPage) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 flex justify-center pointer-events-none" role="dialog" aria-modal="false" aria-labelledby="cookie-consent-title">
        <div className="pointer-events-auto max-h-[70vh] overflow-y-auto w-full flex justify-center items-start">{panel}</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="cookie-consent-title" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-text-primary/70 backdrop-blur-sm" />
      <div className="relative min-h-screen flex items-center justify-center p-4">{panel}</div>
    </div>
  );
};

export default CookieConsent;
